import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { Table } from '../../components/ui/Table';
import { TableActionButton, TableActions } from '../../components/ui';
import { Eye } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { apiFetch } from '@/lib/api';

export function OrgsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [orgs, setOrgs] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch('/api/admin/orgs');
        const json = await res.json();
        if (!json.success) throw new Error(json.error?.message || 'Failed to load');
        
        // Fetch member counts for each org
        const orgsWithCounts = await Promise.all(
          (json.data || []).map(async (org: any) => {
            try {
              const detailRes = await apiFetch(`/api/admin/orgs/${org.id}`);
              const detailJson = await detailRes.json();
              return {
                ...org,
                memberCount: detailJson.success ? detailJson.data.memberCount : 0
              };
            } catch {
              return { ...org, memberCount: 0 };
            }
          })
        );
        
        setOrgs(orgsWithCounts);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const columns = [
    {
      key: 'name',
      title: 'Organization',
      sortable: true,
      render: (value: string, record: any) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{record.slug}</div>
        </div>
      )
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value: string) => {
        const variant = value === 'active' ? 'success' : value === 'suspended' ? 'error' : 'warning';
        return <Badge variant={variant}>{value}</Badge>;
      }
    },
    {
      key: 'memberCount',
      title: 'Members',
      sortable: true,
      align: 'center' as const,
      render: (value: number) => (
        <span className="font-medium">{value}</span>
      )
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: 'Actions',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <TableActions>
          <TableActionButton 
            icon={Eye}
            onClick={() => navigate(`/admin/orgs/${record.id}`)}
          >
            View Details
          </TableActionButton>
        </TableActions>
      )
    }
  ];

  const filters = [
    {
      key: 'status',
      label: 'Filter by Status',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Suspended', value: 'suspended' },
        { label: 'Trialing', value: 'trialing' }
      ]
    }
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Organizations"
        description="Manage all organizations on the platform"
      />
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <Table
        data={orgs}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search organizations..."
        filters={filters}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 20, 50]
        }}
        emptyText="No organizations found."
      />
    </PageContainer>
  );
}


